import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Loader2, Calendar, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Reschedule a booking without changing the price.
 * Available to Admin, Sales, and Center Managers on any pending/confirmed
 * booking. Allows date + time + optional bookable change; server-side
 * availability is re-checked and the client is emailed.
 */
export const RescheduleBookingModal = ({ open, onClose, booking, api, venues = [], onDone }) => {
  const [date, setDate] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [bookableId, setBookableId] = useState('');
  const [reason, setReason] = useState('');
  const [bookables, setBookables] = useState([]);
  const [saving, setSaving] = useState(false);
  const [holidays, setHolidays] = useState({}); // date -> name

  const loadBookables = useCallback(async (venueId) => {
    if (!venueId) return;
    try {
      const res = await api.get(`/bookables?venue_id=${venueId}`);
      setBookables(Array.isArray(res.data) ? res.data : (res.data.bookables || []));
    } catch { /* non-blocking */ }
  }, [api]);

  useEffect(() => {
    if (open && booking) {
      setDate(booking.booking_date || '');
      setStart(booking.start_time || '');
      setEnd(booking.end_time || '');
      setBookableId(booking.bookable_id || '');
      setReason('');
      loadBookables(booking.venue_id);
      api.get('/holidays').then((res) => {
        const map = {};
        (res.data || []).forEach((h) => { map[h.date] = h.name; });
        setHolidays(map);
      }).catch(() => {});
    }
  }, [open, booking, loadBookables, api]);

  const submit = async () => {
    if (!date || !start || !end) {
      toast.error('Date, start time and end time are required');
      return;
    }
    if (start >= end) {
      toast.error('Start time must be before end time');
      return;
    }
    if (holidays[date]) {
      toast.error(`Closed on ${date} (${holidays[date]}). Please choose another date.`);
      return;
    }
    setSaving(true);
    try {
      const body = { booking_date: date, start_time: start, end_time: end, reason: reason.trim() || undefined };
      if (bookableId && bookableId !== booking.bookable_id) body.bookable_id = bookableId;
      await api.patch(`/bookings/${booking.id}/reschedule`, body);
      toast.success('Booking rescheduled. Client has been notified by email.');
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reschedule failed');
    } finally {
      setSaving(false);
    }
  };

  if (!booking) return null;
  const bookableChanged = bookableId && bookableId !== booking.bookable_id;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="reschedule-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Calendar className="w-4 h-4" /> Reschedule booking</DialogTitle>
          <p className="text-xs text-muted-foreground mt-1">
            If the new slot is unpaid, the amount is recalculated for the new duration/space. Paid bookings keep their original amount. The client will be notified by email.
          </p>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="p-3 rounded-md bg-muted/40 text-xs space-y-1">
            <p><span className="text-muted-foreground">Guest: </span><b>{booking.guest_name || booking.user_name || booking.organization_name || 'Client'}</b></p>
            <p><span className="text-muted-foreground">Current: </span>
              {booking.bookable_name} · {booking.booking_date} · {booking.start_time}–{booking.end_time}
            </p>
          </div>

          <div>
            <Label>Space</Label>
            <Select value={bookableId} onValueChange={setBookableId}>
              <SelectTrigger data-testid="reschedule-bookable"><SelectValue placeholder="Same as current" /></SelectTrigger>
              <SelectContent>
                {bookables.map(b => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.name} {b.id === booking.bookable_id ? '(current)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>New date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} min={new Date().toISOString().slice(0, 10)} data-testid="reschedule-date" />
              {date && holidays[date] && (
                <p className="text-xs text-red-500 mt-1">Closed — {holidays[date]}</p>
              )}
            </div>
            <div>
              <Label>Start</Label>
              <Input type="time" value={start} onChange={(e) => setStart(e.target.value)} data-testid="reschedule-start" />
            </div>
            <div>
              <Label>End</Label>
              <Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="reschedule-end" />
            </div>
          </div>

          {bookableChanged && (
            <p className="text-xs text-amber-600 flex items-center gap-1">
              <ArrowRight className="w-3 h-3" /> Space will change to <b>{bookables.find(b => b.id === bookableId)?.name}</b>
            </p>
          )}

          <div>
            <Label>Reason (optional)</Label>
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Captured on the reschedule log" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="reschedule-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Reschedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
